import React, { useContext, useEffect, useState } from 'react';
import { Button } from 'react-bootstrap';
import DatePicker from 'react-datepicker';
import dayjs from 'dayjs';
import AppContext from 'context/Context';
import PageHeader from 'components/common/PageHeader';
import { swalToastError } from 'components/utilities/SwalToastUtil';

import './ProductionOverviewDashboard.css';
import {
  getAncillariesProduction,
  getCompositeDoorGearing,
  getHardwarePacking,
  getHardwareProduction,
  getNumberOfFrameBoxes,
  getNumberOfPieces,
  getOutputPerformance,
  getOutputPerformanceMetadata,
  getProductionErrors,
  getProductionItems,
  getProductionMCs,
  getProductionQCs,
  getProductionSCs,
  getRealdyToLoad,
  getWeldedCillItems
} from 'services/productionOverviewService';

import { transformProductionItemsData } from './ProductionDashboardHelpers';

import SummaryTab from './tabs/SummaryTab';
import PeopleTab from './tabs/PeopleTab';
import PerformanceTab from './tabs/PerformanceTab';
import ThroughputTab from './tabs/ThroughputTab';
import DeliveryTab from './tabs/DeliveryTab';
import MachineTab from './tabs/MachineTab';
import OutputPerformanceTab from './tabs/OutputPerformanceTab';
import ShiftsThroughputTab from './tabs/ShiftsThroughputTab';
import StationsDetailThroughputTab from './tabs/StationsDetailThroughputTab';
import { buildOutputPerformanceFactoriesFromMetadata } from './output-performance/outputPerformanceConfig';

const PLACEHOLDER_TABS = [
  'Cost',
  'Environment'
];

const TABS = [
  'Summary',
  'Safety',
  'Quality',
  'Delivery',
  'Cost',
  'People',
  'Machine',
  'Environment',
  'Output Performance',
  'Production Errors',
  'Throughput',
  'Shifts Throughput',
  'Stations Detail Throughput',
];

function PlaceholderTab() {
  return <div className="production-empty-tab" />;
}

export default function ProductionOverviewDashboard() {
  const [date, setDate] = useState(dayjs(new Date()).format('DD/MM/YYYY'));
  const [productionItems, setProductionItems] = useState([]);
  const [qualityChecks, setQualityChecks] = useState([]);
  const [safetyChecks, setSafetyChecks] = useState([]);
  const [machineChecks, setMachineChecks] = useState([]);
  const [productionErrors, setProductionErrors] = useState([]);
  const [ancillaryItems, setAncillaryItems] = useState([]);
  const [hardwareItems, setHardwareItems] = useState([]);
  const [outputPerformanceItems, setOutputPerformanceItems] = useState([]);
  const [outputPerformanceMetadataFactories, setOutputPerformanceMetadataFactories] = useState([]);
  const [numberOfPiecesItems, setNumberOfPiecesItems] = useState([]);
  const [numberOfFrameBoxesItems, setNumberOfFrameBoxesItems] = useState([]);
  const [weldedCillItems, setWeldedCillItems] = useState([]);
  const [compositeDoorGearingItems, setCompositeDoorGearingItems] = useState([]);
  const [hardwarePackingItems, setHardwarePackingItems] = useState([]);
  const [readlyToLoadItems, setReadlyToLoadItems] = useState([]);
  const [timer, setTimer] = useState();
  const [activeTab, setActiveTab] = useState('Safety');

  const {
    config: { isFluid, isNavbarVerticalCollapsed },
    setConfig
  } = useContext(AppContext);
  const [kanbanIsFluid] = useState(isFluid);
  const [kanbanIsNavbarVerticalCollapsed] = useState(isNavbarVerticalCollapsed);

  useEffect(() => {
    setConfig('isFluid', true);
    setConfig('isNavbarVerticalCollapsed', true);

    return () => {
      setConfig('isFluid', kanbanIsFluid);
      setConfig('isNavbarVerticalCollapsed', kanbanIsNavbarVerticalCollapsed);
    };
  }, []);

  useEffect(() => {
    loadProductionData();
    loadQualityCheckData();
    loadSafetyCheckData();
    loadMachineCheckData();
    loadProductionErrorData();
    loadAncillaryItems();
    loadHardwareItems();
    loadOutputPerformanceItems();
    loadRealdyToLoadItems();
    loadNumberOfPiecesItems();
    loadNumberOfFrameBoxesItems();
    loadWeldedCillItems();
    loadCompositeDoorGearingItems();
    loadHardwarePackingItems();
    loadOutputPerformanceMetadata();
    autoRefresh();

    return () => {
      clearInterval(timer);
    };
  }, []);

  const getCurrentDate = () => {
    return dayjs(date, 'DD/MM/YYYY').format('YYYY-MM-DD');
  };

  const autoRefresh = () => {
    const interval = setInterval(() => {
      loadProductionData();
      loadQualityCheckData();
      loadSafetyCheckData();
      loadMachineCheckData();
      loadProductionErrorData();
      loadAncillaryItems();
      loadHardwareItems();
      loadOutputPerformanceItems();
      loadRealdyToLoadItems();
      loadNumberOfPiecesItems();
      loadNumberOfFrameBoxesItems();
      loadWeldedCillItems();
      loadCompositeDoorGearingItems();
      loadHardwarePackingItems();
    }, 30 * 1000);

    setTimer(interval);
  };

  const loadProductionData = () => {
    getProductionItems(getCurrentDate())
      .then(result => {
        if (result && result.data && result.data.length > 0) {
          const dataTransformed = transformProductionItemsData(result.data);
          setProductionItems(dataTransformed);
        } else {
          setProductionItems([]);
        }
      })
      .catch(err => {
        swalToastError(`Error fetching production items! ${err.message}`);
      });
  };

  const loadOutputPerformanceItems = () => {
    // Backend endpoint returns already-aggregated rows; keep isolated from legacy raw production items.
    getOutputPerformance(getCurrentDate())
      .then(result => {
        const records = result?.data?.data || result?.data || [];
        setOutputPerformanceItems(Array.isArray(records) ? records : []);
      })
      .catch(err => {
        // Do not clear existing data on transient refresh failures.
        // eslint-disable-next-line no-console
        console.error('Error fetching output performance items', err);
      });
  };

    const loadRealdyToLoadItems = () => {
    // Backend endpoint returns already-aggregated rows; keep isolated from legacy raw production items.
    getRealdyToLoad(getCurrentDate())
      .then(result => {
        const records = result?.data?.data || result?.data || [];
        setReadlyToLoadItems(Array.isArray(records) ? records : []);
      })
      .catch(err => {
        // Do not clear existing data on transient refresh failures.
        // eslint-disable-next-line no-console
        console.error('Error fetching output performance items', err);
      });
  };


  const loadNumberOfPiecesItems = () => {
    getNumberOfPieces(getCurrentDate())
      .then(result => {
        const records = result?.data?.data || result?.data || [];
        setNumberOfPiecesItems(Array.isArray(records) ? records : []);
      })
      .catch(err => {
        // eslint-disable-next-line no-console
        console.error('Error fetching number of pieces', err);
      });
  };

  const loadNumberOfFrameBoxesItems = () => {
    getNumberOfFrameBoxes(getCurrentDate())
      .then(result => {
        const records = result?.data?.data || result?.data || [];
        setNumberOfFrameBoxesItems(Array.isArray(records) ? records : []);
      })
      .catch(err => {
        // eslint-disable-next-line no-console
        console.error('Error fetching number of frame boxes', err);
      });
  };

  const loadWeldedCillItems = () => {
    getWeldedCillItems(getCurrentDate())
      .then(result => {
        const records = result?.data?.data || result?.data || [];
        setWeldedCillItems(Array.isArray(records) ? records : []);
      })
      .catch(err => {
        // eslint-disable-next-line no-console
        console.error('Error fetching welded cill items', err);
      });
  };

  const loadCompositeDoorGearingItems = () => {
    getCompositeDoorGearing(getCurrentDate())
      .then(result => {
        const records = result?.data?.data || result?.data || [];
        setCompositeDoorGearingItems(Array.isArray(records) ? records : []);
      })
      .catch(err => {
        // eslint-disable-next-line no-console
        console.error('Error fetching composite door gearing items', err);
      });
  };

  const loadHardwarePackingItems = () => {
    getHardwarePacking(getCurrentDate())
      .then(result => {
        const records = result?.data?.data || result?.data || [];
        setHardwarePackingItems(Array.isArray(records) ? records : []);
      })
      .catch(err => {
        // eslint-disable-next-line no-console
        console.error('Error fetching hardware packing items', err);
      });
  };

  const loadOutputPerformanceMetadata = () => {
    getOutputPerformanceMetadata()
      .then(result => {
        const factories = buildOutputPerformanceFactoriesFromMetadata(result?.data);
        setOutputPerformanceMetadataFactories(factories);
      })
      .catch(err => {
        // Keep existing metadata if a refresh/load retry fails.
        // eslint-disable-next-line no-console
        console.error('Error fetching output performance metadata', err);
      });
  };

  const loadSafetyCheckData = () => {
    getProductionSCs(getCurrentDate())
      .then(result => {
        if (result && result.data && result.data.length > 0) {
          setSafetyChecks(result.data);
        } else {
          setSafetyChecks([]);
        }
      })
      .catch(err => {
        swalToastError(`Error fetching safety checks! ${err.message}`);
      });
  };

  const loadQualityCheckData = () => {
    getProductionQCs(getCurrentDate())
      .then(result => {
        if (result && result.data && result.data.length > 0) {
          setQualityChecks(result.data);
        } else {
          setQualityChecks([]);
        }
      })
      .catch(err => {
        swalToastError(`Error fetching quality checks! ${err.message}`);
      });
  };

  const loadMachineCheckData = () => {
    getProductionMCs(getCurrentDate())
      .then(result => {
        if (result && result.data && result.data.length > 0) {
          setMachineChecks(result.data);
        } else {
          setMachineChecks([]);
        }
      })
      .catch(err => {
        swalToastError(`Error fetching machine checks! ${err.message}`);
      });
  };

  const loadProductionErrorData = () => {
    getProductionErrors(getCurrentDate())
      .then(result => {
        if (result && result.data && result.data.length > 0) {
          setProductionErrors(result.data);
        } else {
          setProductionErrors([]);
        }
      })
      .catch(err => {
        swalToastError(`Error fetching production errors! ${err.message}`);
      });
  };

  const loadAncillaryItems = () => {
    getAncillariesProduction(getCurrentDate())
      .then(result => {
        if (result && result.data && result.data.length > 0) {
          setAncillaryItems(result.data);
        } else {
          setAncillaryItems([]);
        }
      })
      .catch(err => {
        swalToastError(`Error fetching ancillary items! ${err.message}`);
      });
  };

  const loadHardwareItems = () => {
    getHardwareProduction(getCurrentDate())
      .then(result => {
        if (result && result.data && result.data.length > 0) {
          setHardwareItems(result.data);
        } else {
          setHardwareItems([]);
        }
      })
      .catch(err => {
        swalToastError(`Error fetching hardware items! ${err.message}`);
      });
  };

  const onSearch = () => {
    clearInterval(timer);
    loadProductionData();
    loadQualityCheckData();
    loadSafetyCheckData();
    loadMachineCheckData();
    loadProductionErrorData();
    loadAncillaryItems();
    loadHardwareItems();
    loadOutputPerformanceItems();
    loadRealdyToLoadItems()
    loadNumberOfPiecesItems();
    loadNumberOfFrameBoxesItems();
    loadWeldedCillItems();
    loadCompositeDoorGearingItems();
    loadHardwarePackingItems();
    if (dayjs(date, 'DD/MM/YYYY').isSame(new Date(), 'day')) {
      autoRefresh();
    }
  };

  return (
    <div className="production-dashboard-container">
      <PageHeader
        title="Production Overview Dashboard"
        description={`Productions are scanned at the date.`}
        className="mb-3"
      />

      <div className="scanned-date-section d-flex align-items-center justify-content-between flex-wrap">
        <div className="d-flex align-items-center gap-4">
          <div className="scanned-date-info">
            <h5>Scanned Date</h5>
            <span>The data from 22:00 to 22:00</span>
          </div>

          <div style={{ width: '200px' }}>
            <DatePicker
              calendarStartDay={1}
              className="form-control"
              placeholderText="Select Date"
              dateFormat={'dd/MM/yyyy'}
              onChange={date => {
                setDate(dayjs(date).format('DD/MM/YYYY'));
              }}
              selected={new Date()}
              value={date}
            />
          </div>

          <Button variant="primary" onClick={onSearch}>
            Apply
          </Button>
        </div>

        <div className="text-end">
          <div style={{ fontSize: 14, color: '#6c757d', fontWeight: 500 }}>
            Latest Updated At: {dayjs(new Date()).format('hh:mm:ss DD/MM/YYYY')}
          </div>
        </div>
      </div>

      <div className="production-tabs">
        {TABS.map(tab => (
          <div
            key={tab}
            className={`production-tab-item ${
              activeTab === tab ? 'active' : ''
            }`}
            onClick={() => setActiveTab(tab)}
          >
            {tab}
          </div>
        ))}
      </div>

      <div className="tab-content-area">
        {activeTab === 'Summary' && (
          <SummaryTab productionItems={productionItems} />
        )}

        {activeTab === 'Quality' && (
          <PerformanceTab
            title="Quality Checks"
            data={qualityChecks}
            detailPrefix="Operator Check"
            noDataMessage="No quality check data available for this date."
          />
        )}

        {activeTab === 'Delivery' && (
          <DeliveryTab productionItems={productionItems} />
        )}
        
        {activeTab === 'Safety' && (
          <PerformanceTab
            title="Safety Performance"
            data={safetyChecks}
            detailPrefix="Operator Check"
            noDataMessage="No safety performance data available for this date."
          />
        )}

        {activeTab === 'Production Errors' && (
          <PerformanceTab
            title="Production Errors"
            data={productionErrors}
            detailPrefix="Error At"
            noDataMessage="No production error data available for this date."
          />
        )}

        {activeTab === 'Machine' && (
          <MachineTab machineChecks={machineChecks} />
        )}

        {activeTab === 'Output Performance' && (
          <OutputPerformanceTab
            date={getCurrentDate()}
            metadataFactories={outputPerformanceMetadataFactories}
            outputPerformanceItems={outputPerformanceItems}
            ancillaryItems={ancillaryItems}
            hardwareItems={hardwareItems}
            numberOfPiecesItems={numberOfPiecesItems}
            numberOfFrameBoxesItems={numberOfFrameBoxesItems}
            weldedCillItems={weldedCillItems}
            compositeDoorGearingItems={compositeDoorGearingItems}
            hardwarePackingItems={hardwarePackingItems}
            readlyToLoadItems={readlyToLoadItems}
          />
        )}

        {activeTab === 'Throughput' && (
          <ThroughputTab
            productionItems={productionItems}
            ancillaryItems={ancillaryItems}
            hardwareItems={hardwareItems}
          />
        )}

        {activeTab === 'Shifts Throughput' && (
          <ShiftsThroughputTab
            productionItems={productionItems}
            ancillaryItems={ancillaryItems}
            hardwareItems={hardwareItems}
          />
        )}

        {activeTab === 'Stations Detail Throughput' && (
          <StationsDetailThroughputTab
            productionItems={productionItems}
            ancillaryItems={ancillaryItems}
            hardwareItems={hardwareItems}
          />
        )}

        {PLACEHOLDER_TABS.includes(activeTab) && (
          <PlaceholderTab />
        )}

        {activeTab === 'People' && (
          <PeopleTab
            qualityChecks={qualityChecks}
            safetyChecks={safetyChecks}
            productionErrors={productionErrors}
          />
        )}
      </div>
    </div>
  );
}
